import { TableBody, TableRow, TableCell } from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import CurrentActions from "./current-actions";
import type {
  TableColumn,
  TableAction,
  ValidTableNames,
  TableItemType,
} from "@/hooks/table/types";

interface DataTableBodyProps<T extends ValidTableNames = any> {
  sortedData: TableItemType<T>[];
  selectedRows: Set<string | number>;
  enableRowSelection?: boolean;
  displayColumns: TableColumn<T>[];
  handleRowSelection: (rowId: string | number) => void;
  getRowId: (row: TableItemType<T>) => string | number;
  rowActions?: (row: TableItemType<T>) => TableAction<T>[];
}

export default function DataTableBody<T extends ValidTableNames = any>({
  sortedData,
  selectedRows,
  enableRowSelection,
  displayColumns,
  handleRowSelection,
  getRowId,
  rowActions,
}: DataTableBodyProps<T>) {
  return (
    <TableBody>
      {sortedData.map((row, index) => {
        const rowId = getRowId(row);
        const isSelected = selectedRows.has(rowId);
        const currentActions = rowActions ? rowActions(row) : undefined;

        return (
          <TableRow
            key={row._id || index}
            className={isSelected ? "bg-zinc-200/80 dark:bg-black/20" : ""}
          >
            {enableRowSelection && (
              <TableCell className="w-12! h-10! p-0! [&>[role=checkbox]]:translate-y-0!">
                <div className="flex items-center justify-center h-full w-full">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleRowSelection(rowId)}
                    aria-label={`Seleccionar fila ${index + 1}`}
                  />
                </div>
              </TableCell>
            )}
            {displayColumns.map((column) => (
              <TableCell
                key={column.key as string}
                className={column.className}
              >
                <div className="space-y-1 text-xs">
                  {column.render
                    ? column.render(
                        row[column.key as keyof TableItemType<T>],
                        row,
                      )
                    : (row[column.key as keyof TableItemType<T>] as any)}
                </div>
              </TableCell>
            ))}
            {currentActions && (
              <TableCell className="sticky right-0 z-10 p-0 bg-background">
                <CurrentActions
                  row={row as any}
                  actions={currentActions as any}
                />
              </TableCell>
            )}
          </TableRow>
        );
      })}
    </TableBody>
  );
}
